// Vue
import { Component, Watch } from "vue-property-decorator";

// Template
import template from "./template.vue";

import ModuleWrapper from "#/components/module_wrapper/ModuleWrapper";
import ModuleBase from "#/common/ModuleBase";

import { Api } from "#/api";

import Highcharts from "highcharts/highmaps";
import { IHistoricalData } from "../../../common/interfaces/kpi/FilterHistoricalDataResponse";
import {
  IDiagramSet,
  IDiagramSetPoint,
} from "../../../common/interfaces/DiagramSet";

@Component({
  mixins: [template],
  components: {
    ModuleWrapper,
  },
})
export class HistoryModule extends ModuleBase {
  private colors = ["#0088c6", "#4dc6f4", "#999999"];

  get variantOption() {
    return this.instance["section_diagram_option"]["variant_option"];
  }
  get diagramType() {
    return this.instance["section_diagram_option"]["diagram_type"];
  }
  get intervalOption() {
    return this.instance["section_diagram_option"]["interval_option"];
  }
  get graphLength() {
    return this.instance["section_diagram_option"]["length"];
  }
  get labelXAxis() {
    return this.instance["section_diagram_option"]["label_x_axis"];
  }
  get labelYAxis() {
    return this.instance["section_diagram_option"]["label_y_axis"];
  }
  get diagramSets() {
    return this.instance["section_diagramset"]["diagram_sets"];
  }

  async mounted() {
    if (this.variantOption === "dynamic") {
      const response = await this.loadHistoricalData(this.diagramSets);
      const formattedSets = this.buildDiagramSet(response, this.diagramSets);

      if (this.diagramType === "pie")
        this.renderPieChart(
          formattedSets,
          this.diagramType,
          this.backgroundColor,
          this.labelXAxis,
          this.labelYAxis
        );
      else
        this.renderChart(
          formattedSets,
          this.diagramType,
          this.backgroundColor,
          this.labelXAxis,
          this.labelYAxis
        );
    } else {
      const formattedValues = this.diagramSets.map(
        (diagramSet) => diagramSet.diagram_manual_values
      );

      const formattedSets = this.buildDiagramSet(
        formattedValues,
        this.diagramSets
      );

      if (this.diagramType === "pie")
        this.renderPieChart(
          formattedSets,
          this.diagramType,
          this.backgroundColor,
          this.labelXAxis,
          this.labelYAxis
        );
      else
        this.renderChart(
          formattedSets,
          this.diagramType,
          this.backgroundColor,
          this.labelXAxis,
          this.labelYAxis
        );
    }
  }

  getBackgroundColorFromInstance(instance: object) {
    return instance["section_color"]["color"];
  }

  async loadHistoricalData(diagramSets: IDiagramSet[]) {
    const promises: any[] = [];

    diagramSets.forEach((set) => {
      const historicalDataObject: IHistoricalData = {
        filterId: set["diagram_service_value"]["filter"]["FILTER"],
        interval: this.intervalOption,
        length: this.graphLength,
        dayOfWeek: set["dayOfWeek"],
        dayOfMonth: set["dayOfMonth"],
        month: set["month"],
      };

      promises.push(
        Api.kpiService.getFilterHistoricalData(historicalDataObject)
      );
    });

    try {
      return await Promise.all(promises);
    } catch (e) {
      this.$store.ui.error(e);
      return [];
    }
  }

  buildDiagramSet(pointsArray: IDiagramSetPoint[][], diagramSets: any[]) {
    return pointsArray.map((points, index) => {
      const formattedData = points.map((point) => {
        return {
          name: point.label,
          y: point.count,
        };
      });

      return {
        color: diagramSets.length <= 3 ? this.colors[index] : "",
        name: diagramSets[index]["set_name"],
        data: formattedData as any[],
      } as IDiagramSet;
    });
  }

  renderLabels(sets) {
    return sets.map((datas) => {
      return datas.data.map((data) => {
        return data.name;
      });
    });
  }

  renderChart(sets, type, backgroundColor, labelXAxis, labelYAxis) {
    let element = this.$refs["chart"] as any;
    let labels: any = [];
    labels = this.renderLabels(sets);

    Highcharts.chart(element, {
      chart: {
        type,
        marginTop: 80,
        backgroundColor:
          this.instance["section_color"]["color"] == "black"
            ? "transparent"
            : "#ffffff",
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
      } as any,
      legend: {
        itemStyle: {
          color: backgroundColor == "black" ? "#ffffff" : "#000000",
          cursor: "pointer",
          fontSize: "12px",
        },
        itemHoverStyle: {
          color: backgroundColor == "black" ? "#ffffff" : "#000000",
        },
      },
      title: {
        text: "",
      },
      xAxis: {
        allowDecimals: false,
        title: {
          text: labelXAxis,
          style: {
            color: backgroundColor == "black" ? "#ffffff" : "#000000",
          },
        },
        labels: {
          enabled: true,
          formatter: function () {
            return labels[0][this.value];
          },
          style: {
            color: backgroundColor == "black" ? "#ffffff" : "#000000",
          },
        },
      },
      yAxis: {
        allowDecimals: false,
        title: {
          text: labelYAxis,
          style: {
            color: backgroundColor == "black" ? "#ffffff" : "#000000",
          },
        },
        labels: {
          style: {
            color: backgroundColor == "black" ? "#ffffff" : "#000000",
          },
        },
      },
      tooltip: {
        formatter: function () {
          return "<b>" + this.series.name + "</b><br/>" + this.point.y + " ";
        },
      },
      series: [...sets],
    });
  }

  renderPieChart(sets, type, backgroundColor, labelXAxis, labelYAxis) {
    let element = this.$refs["chart"] as any;
    let labels: any = [];
    labels = this.renderLabels(sets);

    const rowDiv = document.createElement("div");
    rowDiv.classList.add("row");
    element.appendChild(rowDiv);

    sets.map((set) => {
      const colDiv = document.createElement("div");
      colDiv.classList.add("col-md-6", "col-sm-12");
      rowDiv.appendChild(colDiv);

      Highcharts.chart(colDiv, {
        chart: {
          type: "pie",
          marginTop: 80,
          backgroundColor:
            this.instance["section_color"]["color"] == "black"
              ? "transparent"
              : "#ffffff",
          plotBackgroundColor: null,
          plotBorderWidth: null,
          plotShadow: false,
        } as any,
        legend: {
          itemStyle: {
            color: backgroundColor == "black" ? "#ffffff" : "#000000",
            cursor: "pointer",
            fontSize: "12px",
          },
          itemHoverStyle: {
            color: backgroundColor == "black" ? "#ffffff" : "#000000",
          },
        },
        title: {
          text: "",
        },
        xAxis: {
          allowDecimals: false,
          title: {
            text: labelXAxis,
            style: {
              color: backgroundColor == "black" ? "#ffffff" : "#000000",
            },
          },
          labels: {
            enabled: true,
            formatter: function () {
              return labels[0][this.value];
            },
            style: {
              color: backgroundColor == "black" ? "#ffffff" : "#000000",
            },
          },
        },
        yAxis: {
          allowDecimals: false,
          title: {
            text: labelYAxis,
            style: {
              color: backgroundColor == "black" ? "#ffffff" : "#000000",
            },
          },
          labels: {
            style: {
              color: backgroundColor == "black" ? "#ffffff" : "#000000",
            },
          },
        },
        tooltip: {
          formatter: function () {
            return "<b>" + this.series.name + "</b><br/>" + this.point.y + " ";
          },
        },
        plotOptions: {
          pie: {
            size: "100%",
            allowPointSelect: true,
            cursor: "pointer",
            dataLabels: {
              enabled: true,
              color: backgroundColor == "black" ? "#ffffff" : "#000000",
            },
          },
        },
        series: [set],
      });
    });
  }
}
